import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { CasePrepUscisFormsAPI } from "../api";

/**
 * One authenticated fetch of each cited source document, shared by every field
 * that cites it.
 *
 * A single evidence document routinely fills several form fields, and each cited
 * field would otherwise re-download the same rendition. Mirrors the intake
 * assistant's `CitationDocumentsContext`, but keyed by source document id and
 * fetched through the authenticated case-prep endpoint rather than a public
 * intake token.
 */

type DocumentState =
  | { status: "loading"; url: null }
  | { status: "ready"; url: string }
  | { status: "unavailable"; url: null };

const UNAVAILABLE: DocumentState = { status: "unavailable", url: null };

type FormCitationDocuments = {
  request: (sourceDocumentId: string) => void;
  documents: Record<string, DocumentState>;
};

const FormCitationDocumentsContext =
  createContext<FormCitationDocuments | null>(null);

export function FormCitationDocumentsProvider({
  individualId,
  filledFormId,
  children
}: {
  individualId: string;
  filledFormId: string;
  children: React.ReactNode;
}) {
  const [documents, setDocuments] = useState<Record<string, DocumentState>>({});
  const requested = useRef<Set<string>>(new Set());
  const objectUrls = useRef<string[]>([]);

  // A different form's citations are a different set; drop what we hold so a
  // stale rendition never shows against the wrong form.
  useEffect(() => {
    requested.current = new Set();
    setDocuments({});
    return () => {
      objectUrls.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrls.current = [];
    };
  }, [individualId, filledFormId]);

  const request = useCallback(
    (sourceDocumentId: string) => {
      if (
        !individualId ||
        !filledFormId ||
        !sourceDocumentId ||
        requested.current.has(sourceDocumentId)
      )
        return;
      requested.current.add(sourceDocumentId);

      setDocuments((current) => ({
        ...current,
        [sourceDocumentId]: { status: "loading", url: null }
      }));

      CasePrepUscisFormsAPI.getFieldCitationPdf(
        individualId,
        filledFormId,
        sourceDocumentId
      )
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          objectUrls.current.push(url);
          setDocuments((current) => ({
            ...current,
            [sourceDocumentId]: { status: "ready", url }
          }));
        })
        .catch(() => {
          // Expected for fields filled before the server kept a rendition, so
          // this is a fallback (quote the snippet) rather than an error to show.
          setDocuments((current) => ({
            ...current,
            [sourceDocumentId]: UNAVAILABLE
          }));
        });
    },
    [individualId, filledFormId]
  );

  const value = useMemo(() => ({ request, documents }), [request, documents]);

  return (
    <FormCitationDocumentsContext.Provider value={value}>
      {children}
    </FormCitationDocumentsContext.Provider>
  );
}

/**
 * The cited source document, fetched on first use.
 *
 * Reports `unavailable` outside a provider as well as on failure, so a caller
 * without one falls back to quoting the snippet rather than breaking.
 */
export function useFormCitationDocument(
  sourceDocumentId: string | null | undefined,
  enabled = true
): DocumentState {
  const context = useContext(FormCitationDocumentsContext);

  useEffect(() => {
    if (!enabled || !sourceDocumentId || !context) return;
    context.request(sourceDocumentId);
  }, [sourceDocumentId, context, enabled]);

  if (!enabled || !sourceDocumentId || !context) return UNAVAILABLE;
  return (
    context.documents[sourceDocumentId] ?? { status: "loading", url: null }
  );
}
